import React, { useState, useEffect } from 'react';
import { Search, MessageSquare, Clock, User, Trash2, X, AlertTriangle, MapPin, Zap, Users2 } from 'lucide-react';
import axios from 'axios';

interface Message {
  id: number;
  message_type: string;
  content: string;
  emoji: string;
  sender_username: string;
  sender_name?: string;
  family_name: string;
  created_at: string;
  sender_language?: string;
}

// Stat Card
const StatCard: React.FC<{ icon: React.ReactNode; value: number; label: string; color: string }> = ({ icon, value, label, color }) => {
  const colors: Record<string, string> = {
    green: 'from-green-50 to-green-100 border-green-200 text-green-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    red: 'from-red-50 to-red-100 border-red-200 text-red-600',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-2.5 text-center`}>
      <div className="flex justify-center mb-1 opacity-60 [&>svg]:w-4 [&>svg]:h-4">{icon}</div>
      <p className="text-xl font-semibold">{value}</p>
      <p className="text-[10px] opacity-60 uppercase tracking-wide">{label}</p>
    </div>
  );
};

const getFlag = (lang?: string) => {
  const f: Record<string, string> = { 'tr': '🇹🇷', 'en': '🇬🇧', 'de': '🇩🇪', 'es': '🇪🇸', 'fr': '🇫🇷', 'it': '🇮🇹', 'pt': '🇵🇹', 'nl': '🇳🇱', 'pl': '🇵🇱', 'ru': '🇷🇺', 'ar': '🇸🇦', 'zh': '🇨🇳', 'ja': '🇯🇵', 'ko': '🇰🇷', 'hi': '🇮🇳', 'id': '🇮🇩', 'vi': '🇻🇳', 'th': '🇹🇭' };
  return lang ? f[lang] || '🌐' : null;
};

const Messages: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selected, setSelected] = useState<Message | null>(null);
  const [limit, setLimit] = useState(50);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const r = await axios.get('https://api.lifeshare.app/api/v1/admin/messages');
      setMessages(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Bu mesajı silmek istiyor musunuz?')) return;
    setActionLoading(id);
    try {
      await axios.delete(`https://api.lifeshare.app/api/v1/admin/messages/${id}`);
      setMessages(messages.filter(m => m.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (e) { alert('Silme işlemi başarısız'); }
    setActionLoading(null);
  };

  const filtered = messages.filter(m => {
    const q = search.toLowerCase();
    const matchSearch = m.content.toLowerCase().includes(q) || m.sender_username.toLowerCase().includes(q) || m.sender_name?.toLowerCase().includes(q) || m.family_name.toLowerCase().includes(q);
    const matchFilter = filter === 'all' || m.message_type === filter;
    return matchSearch && matchFilter;
  }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const quickC = messages.filter(m => m.message_type === 'quick').length;
  const emergencyC = messages.filter(m => m.message_type === 'emergency').length;
  const locationC = messages.filter(m => m.message_type === 'location').length;

  const getTypeStyle = (type: string) => {
    switch (type) {
      case 'quick': return 'bg-blue-100 text-blue-700';
      case 'emergency': return 'bg-red-100 text-red-700';
      case 'location': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'quick': return 'Hızlı';
      case 'emergency': return 'Acil';
      case 'location': return 'Konum';
      default: return type;
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Mesajlar</h1>
          <p className="text-sm text-gray-500 mt-1">{messages.length} mesaj</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={<MessageSquare />} value={messages.length} label="Toplam" color="purple" />
          <StatCard icon={<Zap />} value={quickC} label="Hızlı" color="blue" />
          <StatCard icon={<AlertTriangle />} value={emergencyC} label="Acil" color="red" />
          <StatCard icon={<MapPin />} value={locationC} label="Konum" color="green" />
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl p-4 mb-6">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Mesaj, gönderen veya çevre ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'Tümü' },
              { value: 'quick', label: 'Hızlı' },
              { value: 'emergency', label: 'Acil' },
              { value: 'location', label: 'Konum' },
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.value ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {f.label}
              </button>
            ))}
            <span className="ml-auto text-xs text-gray-500 self-center">{filtered.length} sonuç</span>
          </div>
        </div>

        {/* Messages Grid - Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.slice(0, limit).map(m => {
            const flag = getFlag(m.sender_language);
            return (
              <div key={m.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-200 transition-all cursor-pointer" onClick={() => setSelected(m)}>
                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">{m.emoji || '💬'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${getTypeStyle(m.message_type)}`}>
                        {getTypeText(m.message_type)}
                      </span>
                      <span className="text-xs text-gray-400">#{m.id}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 line-clamp-2">{m.content}</p>
                  </div>
                </div>

                {/* Sender & Family */}
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <div className="flex items-center gap-1 truncate">
                    <User className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{m.sender_name || m.sender_username}</span>
                    {flag && <span>{flag}</span>}
                  </div>
                  <div className="flex items-center gap-1 truncate">
                    <Users2 className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{m.family_name}</span>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs text-gray-400">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(m.created_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                  <span>{new Date(m.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Load More */}
        {filtered.length > limit && (
          <div className="text-center mt-6">
            <button onClick={() => setLimit(l => l + 50)} className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors">
              Daha fazla yükle ({limit} / {filtered.length})
            </button>
          </div>
        )}

        {/* Empty */}
        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Mesaj bulunamadı</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50" onClick={() => setSelected(null)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl max-h-[90vh] overflow-auto rounded-t-3xl" onClick={e => e.stopPropagation()}>

            {/* Handle */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-9 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <span className="text-2xl">{selected.emoji || '💬'}</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Mesaj #{selected.id}</p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTypeStyle(selected.message_type)}`}>
                    {getTypeText(selected.message_type)}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="px-5 py-4 space-y-3">
              {/* Message */}
              <div className="p-4 bg-purple-50 rounded-xl">
                <p className="text-lg text-gray-900">
                  <span className="text-2xl mr-2">{selected.emoji}</span>
                  {selected.content}
                </p>
              </div>

              {/* Sender */}
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-2">Gönderen</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-purple-600 font-semibold">{(selected.sender_name || selected.sender_username).charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selected.sender_name || selected.sender_username}</p>
                    <p className="text-xs text-gray-500">@{selected.sender_username}</p>
                  </div>
                </div>
              </div>

              {/* Family */}
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Çevre</p>
                <p className="font-medium text-gray-900">� {selected.family_name}</p>
              </div>

              {/* Time */}
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Gönderilme</p>
                <p className="text-gray-900">{new Date(selected.created_at).toLocaleString('tr-TR')}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="p-5 border-t border-gray-100">
              <button
                onClick={() => handleDelete(selected.id)}
                disabled={actionLoading === selected.id}
                className="w-full py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-red-600"
              >
                {actionLoading === selected.id ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Mesajı Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
